import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  useGetTemplatesForEmployeeQuery,
  useGetEmployeeSubmissionsQuery,
  useStartSubmissionMutation,
  useUpdateSubmissionAnswerMutation,
  useSubmitFormMutation,
  useGetSubmissionByIdQuery,
} from "../../services/api";
import "./Values.css";

// Question type renderers
const QuestionRenderer = ({ question, answer, onChange, disabled }) => {
  // Support both old field names (questionType, questionText) and new (type, text)
  const questionType = question.type || question.questionType;
  const questionText = question.text || question.questionText;
  const options = question.options;
  const ratingConfig = question.ratingConfig;
  const isRequired = question.validation?.required || question.isRequired;

  const renderTextShort = () => (
    <input
      type="text"
      className="field-input"
      placeholder="Enter your answer..."
      value={answer || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      maxLength={250}
    />
  );

  const renderTextLong = () => {
    const handleTextareaChange = (e) => {
      onChange(e.target.value);
      // Auto-grow logic: expand up to 300px, then show scrollbar
      e.target.style.height = 'auto';
      const newHeight = Math.min(e.target.scrollHeight, 300);
      e.target.style.height = newHeight + 'px';
      // Show scrollbar when content exceeds max height
      e.target.style.overflowY = e.target.scrollHeight > 300 ? 'auto' : 'hidden';
    };

    return (
      <textarea
        className="field-input textarea auto-grow"
        placeholder="Enter your detailed answer..."
        value={answer || ""}
        onChange={handleTextareaChange}
        disabled={disabled}
        rows={3}
      />
    );
  };

  const renderSingleChoice = () => (
    <div className="choice-group">
      {options?.map((opt, idx) => (
        <label key={idx} className="choice-item">
          <input
            type="radio"
            name={`q-${question._id || question.questionId}`}
            value={opt.value}
            checked={answer === opt.value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          <span className="choice-label">{opt.label}</span>
        </label>
      ))}
    </div>
  );

  const renderMultiChoice = () => {
    const selectedValues = Array.isArray(answer) ? answer : [];
    return (
      <div className="choice-group">
        {options?.map((opt, idx) => (
          <label key={idx} className="choice-item">
            <input
              type="checkbox"
              value={opt.value}
              checked={selectedValues.includes(opt.value)}
              onChange={(e) => {
                const newValues = e.target.checked
                  ? [...selectedValues, opt.value]
                  : selectedValues.filter((v) => v !== opt.value);
                onChange(newValues);
              }}
              disabled={disabled}
            />
            <span className="choice-label">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderRatingScale = () => {
    const minLabel = ratingConfig?.minLabel || "";
    const maxLabel = ratingConfig?.maxLabel || "";
    const likertOptions = [
      { value: 1, label: "Never", shade: "shade-1" },
      { value: 2, label: "Rarely", shade: "shade-2" },
      { value: 3, label: "Occasionally", shade: "shade-3" },
      { value: 4, label: "Frequently", shade: "shade-4" },
      { value: 5, label: "Very Frequently", shade: "shade-5" },
    ];

    return (
      <div className="likert-container">
        <div className="likert-row">
          {likertOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`likert ${opt.shade} ${answer === opt.value ? "active" : ""}`}
              onClick={() => !disabled && onChange(opt.value)}
              disabled={disabled}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="likert-legend">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="question-block">
      <label className="field-label">
        {questionText}
        {isRequired && <span className="required">*</span>}
      </label>
      {questionType === "text_short" && renderTextShort()}
      {questionType === "text_long" && renderTextLong()}
      {questionType === "single_choice" && renderSingleChoice()}
      {questionType === "multi_choice" && renderMultiChoice()}
      {questionType === "rating_scale" && renderRatingScale()}
    </div>
  );
};

// Form List View
const FormListView = ({ templates, submissions, onSelectForm, onContinueForm }) => {
  const getSubmissionStatus = (templateId) => {
    const submission = submissions?.find(
      (s) => s.template === templateId || s.template?._id === templateId
    );
    return submission;
  };

  return (
    <div className="form-list">
      <h2 className="section-heading">Available Forms</h2>
      <p className="helper-text">
        Select a form below to share your insights and examples.
      </p>

      {templates?.length === 0 ? (
        <div className="empty-state">
          <p>No forms have been assigned to you yet.</p>
          <p className="hint">Check back later for new assessments.</p>
        </div>
      ) : (
        <div className="form-cards">
          {templates?.map((template) => {
            const submission = getSubmissionStatus(template._id);
            const isCompleted = submission?.status === "submitted";
            const isInProgress = submission?.status === "draft";

            return (
              <div
                key={template._id}
                className={`form-card ${isCompleted ? "completed" : ""}`}
              >
                <div className="form-card-header">
                  <h3 className="form-card-title">{template.title}</h3>
                  <span className={`form-status ${submission?.status || "new"}`}>
                    {isCompleted
                      ? "Completed"
                      : isInProgress
                      ? "In Progress"
                      : "Not Started"}
                  </span>
                </div>
                <p className="form-card-desc">
                  {template.description || "No description provided."}
                </p>
                <div className="form-card-meta">
                  <span>{template.questions?.length || 0} questions</span>
                </div>
                <div className="form-card-actions">
                  {isCompleted ? (
                    <button className="btn secondary" disabled>
                      Submitted
                    </button>
                  ) : isInProgress ? (
                    <button
                      className="btn primary"
                      onClick={() => onContinueForm(submission._id)}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      className="btn primary"
                      onClick={() => onSelectForm(template._id)}
                    >
                      Start Form
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Form Fill View
const FormFillView = ({
  submissionId,
  onBack,
  onSubmitSuccess,
}) => {
  const { data: submissionData, isLoading, refetch } = useGetSubmissionByIdQuery(
    submissionId,
    { skip: !submissionId }
  );
  const [updateAnswer] = useUpdateSubmissionAnswerMutation();
  const [submitForm, { isLoading: isSubmitting }] = useSubmitFormMutation();

  const [answers, setAnswers] = useState({});
  const [saveStatus, setSaveStatus] = useState("");

  const submission = submissionData;
  const template = submission?.template;
  const questions = template?.questions || [];

  // Initialize answers from submission data
  useEffect(() => {
    if (submission?.answers) {
      const answerMap = {};
      submission.answers.forEach((a) => {
        answerMap[a.questionId] = a.value;
      });
      setAnswers(answerMap);
    }
  }, [submission]);

  // Auto-save with debounce
  const debouncedSave = useCallback(
    async (questionIndex, questionId, value) => {
      if (!submissionId) return;
      setSaveStatus("Saving...");
      try {
        await updateAnswer({
          id: submissionId,
          body: { questionIndex, value },
        }).unwrap();
        setSaveStatus("Saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (err) {
        setSaveStatus("Save failed");
        console.error("Auto-save error:", err);
      }
    },
    [submissionId, updateAnswer]
  );

  const handleAnswerChange = (questionIndex, questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    debouncedSave(questionIndex, questionId, value);
  };

  const handleSubmit = async () => {
    // Validate required questions
    const requiredQuestions = questions.filter((q) => {
      const qData = q.questionId || q.inlineQuestion;
      return qData?.isRequired;
    });

    const missingRequired = requiredQuestions.filter((q) => {
      const qId = q.questionId?._id || q.inlineQuestion?._id || q._id;
      const answer = answers[qId];
      return !answer || (Array.isArray(answer) && answer.length === 0);
    });

    if (missingRequired.length > 0) {
      alert(`Please answer all required questions (${missingRequired.length} remaining)`);
      return;
    }

    try {
      await submitForm({ id: submissionId, body: {} }).unwrap();
      onSubmitSuccess();
    } catch (err) {
      console.error("Submit error:", err);
      alert("Failed to submit form. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="values-container">
        <div className="loading-state">Loading form...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="values-container">
        <div className="error-state">
          <p>Form not found.</p>
          <button className="btn secondary" onClick={onBack}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isSubmitted = submission.status === "submitted";

  return (
    <div className="form-fill-view">
      <div className="form-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Forms
        </button>
        <div className="form-header-content">
          <h1 className="form-title">{template?.title}</h1>
          {template?.description && (
            <p className="form-description">{template.description}</p>
          )}
        </div>
        {saveStatus && <span className="save-status">{saveStatus}</span>}
      </div>

      <div className="values-card">
        {template?.instructions && (
          <div className="form-instructions">
            <h3>Instructions</h3>
            <p>{template.instructions}</p>
          </div>
        )}

        <div className="questions-list">
          {questions.map((q, idx) => {
            const questionData = q.questionId || q.inlineQuestion || q;
            const qId = questionData._id || q._id;
            return (
              <div key={qId || idx} className="question-wrapper">
                <span className="question-number">Q{idx + 1}</span>
                <QuestionRenderer
                  question={questionData}
                  answer={answers[qId]}
                  onChange={(value) => handleAnswerChange(idx, qId, value)}
                  disabled={isSubmitted}
                />
              </div>
            );
          })}
        </div>

        {!isSubmitted && (
          <div className="form-actions">
            <button
              className="btn primary full"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Form"}
            </button>
            <p className="hint center">
              Your progress is automatically saved.
            </p>
          </div>
        )}

        {isSubmitted && (
          <div className="submitted-notice">
            <span className="check-icon">✓</span>
            <p>This form has been submitted. Thank you for your input!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Values Component
export default function Values() {
  const user = useSelector((state) => state.auth.user);
  const company = useSelector((state) => state.auth.company);
  const companyId = company?._id || company?.id;

  const [view, setView] = useState("list"); // list | form
  const [activeSubmissionId, setActiveSubmissionId] = useState(null);

  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useGetTemplatesForEmployeeQuery(
    { companyId },
    { skip: !companyId }
  );

  const {
    data: submissionsData,
    isLoading: submissionsLoading,
    refetch: refetchSubmissions,
  } = useGetEmployeeSubmissionsQuery();

  const [startSubmission] = useStartSubmissionMutation();

  // templatesData is already the templates array from transformResponse
  const templates = templatesData || [];
  const submissions = submissionsData || [];

  const handleSelectForm = async (templateId) => {
    try {
      const result = await startSubmission({ templateId, companyId }).unwrap();
      // API returns { submission: {...} } after transformResponse
      setActiveSubmissionId(result.submission._id);
      setView("form");
    } catch (err) {
      console.error("Start submission error:", err);
      alert("Failed to start form. Please try again.");
    }
  };

  const handleContinueForm = (submissionId) => {
    setActiveSubmissionId(submissionId);
    setView("form");
  };

  const handleBack = () => {
    setView("list");
    setActiveSubmissionId(null);
    refetchSubmissions();
  };

  const handleSubmitSuccess = () => {
    setView("list");
    setActiveSubmissionId(null);
    refetchSubmissions();
  };

  if (!companyId) {
    return (
      <div className="values-container">
        <h1 className="username">Unpack Your Values</h1>
        <div className="empty-state">
          <p>You are not associated with any company yet.</p>
          <p className="hint">Please contact your administrator to be added to a company.</p>
        </div>
      </div>
    );
  }

  if (templatesLoading || submissionsLoading) {
    return (
      <div className="values-container">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="values-container">
      <h1 className="username">Unpack Your Values</h1>
      <p className="values-subtitle">
        Help us understand how your organization's values show up in real,
        everyday situations. Your examples will shape the competency rubric.
      </p>

      {view === "list" ? (
        <FormListView
          templates={templates}
          submissions={submissions}
          onSelectForm={handleSelectForm}
          onContinueForm={handleContinueForm}
        />
      ) : (
        <FormFillView
          submissionId={activeSubmissionId}
          onBack={handleBack}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
    </div>
  );
}
